/** @jsx jsx */
/** @jsxRuntime classic */

import { useMemo } from 'react';
import { Interpolation, jsx } from '@emotion/react';
import useTheme from '../style/useTheme';
import { ThemeConfig } from '../style/theme';
import { ColorResolveOptions } from '../style/colors';

type Size = 'sm' | 'lg' | 'md';

export type Props = React.PropsWithChildren<{
  size?: Size;
  loading?: boolean;
  backgroundColor?: ColorResolveOptions;
  backgroundColorInner?: ColorResolveOptions;
}>;

const FRAME_SIZE_MAP: Record<Size, number> = {
  sm: 48,
  md: 64,
  lg: 80,
};

const FRAME_INNER_SIZE_MAP: Record<Size, number> = {
  sm: 40,
  md: 54,
  lg: 72,
};

const CircularFrame = ({
  children,
  size = 'sm',
  loading,
  backgroundColor,
  backgroundColorInner,
}: Props): JSX.Element => {
  const { theme, resolveColor } = useTheme();

  const loadingImg = useMemo(
    () => (
      <div
        data-testid="loadingImg"
        css={{
          width: '100% !important',
          height: '100% !important',
          borderRadius: '50% !important',
          backgroundColor: resolveColor({
            type: 'theme',
            colorLegacy:
              theme.components.circularFrame.default.emptyColor.legacy,
            colorNext: theme.components.circularFrame.default.emptyColor.next,
          }),
        }}
      />
    ),
    [theme.components.circularFrame.default.emptyColor, resolveColor]
  );

  const borderColor = resolveColor({
    type: 'theme',
    colorLegacy: theme.components.circularFrame.default.borderColor.legacy,
    colorNext: theme.components.circularFrame.default.borderColor.next,
  });

  const frameSize = `${FRAME_SIZE_MAP[size]}px`;
  const innerSize = `${FRAME_INNER_SIZE_MAP[size]}px`;

  const frameCss: Interpolation<ThemeConfig> = {
    border: `1px solid ${borderColor}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    boxSizing: 'border-box',
    height: frameSize,
    minHeight: frameSize,
    width: frameSize,
    minWidth: frameSize,
    ...(backgroundColor && {
      backgroundColor: resolveColor(backgroundColor),
    }),
  };

  const innerCss: Interpolation<ThemeConfig> = {
    height: innerSize,
    width: innerSize,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    '& > img': {
      width: '100% !important',
    },
    ...(backgroundColorInner && {
      backgroundColor: resolveColor(backgroundColorInner),
    }),
  };

  return (
    <div
      css={frameCss}
      data-testid="CircularFrame"
      className="origami-CircularFrame"
    >
      <div
        css={innerCss}
        data-testid="CircularFrame-inner"
        className="origami-CircularFrame-inner"
      >
        {loading ? loadingImg : children}
      </div>
    </div>
  );
};

export default CircularFrame;
